import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { ExtendedChatMessage } from './chat-types';

interface UseConversationScrollArgs {
    messages: ExtendedChatMessage[];
    isFetchingNextPage: boolean;
    isLoadingMessages: boolean;
    conversationRef: MutableRefObject<HTMLDivElement | null>;
    /** Optional sticky-scroll context (shadcn Conversation) used to override the native scroll behaviour. */
    stickyContextRef: MutableRefObject<any>;
}

/**
 * Owns the conversation scroll lifecycle:
 * - **Pagination**: snapshots the first visible message before a page fetch and restores its
 *   pixel offset after React commits, so scroll position doesn't jump.
 * - **New-message highlight**: exposes `paginatedNewIds` for a brief entrance animation on
 *   messages that arrived from pagination (not from streaming).
 * - **Initial scroll-to-bottom**: runs once when messages first render.
 *
 * Callers should invoke `reset()` on session switch to clear the anchor/flag state.
 */
export function useConversationScroll({
    messages,
    isFetchingNextPage,
    isLoadingMessages,
    conversationRef,
    stickyContextRef,
}: UseConversationScrollArgs) {
    /** scrollHeight captured when a pagination fetch starts — used to add the prepended-content delta. */
    const prevScrollHeightRef = useRef<number | null>(null);
    const prevMsgCountRef = useRef<number>(0);
    const hasScrolledToBottomRef = useRef<boolean>(false);
    const [paginatedNewIds, setPaginatedNewIds] = useState<Set<string>>(new Set());

    const getScrollContainer = useCallback((): HTMLElement | null => {
        const fromCtx = stickyContextRef.current?.scrollRef?.current;
        if (fromCtx) return fromCtx;
        const wrapper = conversationRef.current;
        if (!wrapper) return null;
        const logEl = wrapper.querySelector('[role="log"]') as HTMLElement;
        if (!logEl) return null;
        const scrollDiv = logEl.firstElementChild as HTMLElement;
        return scrollDiv ?? null;
    }, [conversationRef, stickyContextRef]);

    // ── Scroll preservation (pagination) ──────────────────────────────
    // Claude-style: while a page fetch is in flight, continuously snapshot scrollHeight so
    // any streaming content added to the bottom doesn't pollute the baseline. When the fetch
    // resolves and the prepended messages land, bump scrollTop by (newHeight - lastSnapshot).
    useLayoutEffect(() => {
        const container = getScrollContainer();
        if (!container) return;

        if (isFetchingNextPage) {
            prevScrollHeightRef.current = container.scrollHeight;
            prevMsgCountRef.current = messages.length;
            stickyContextRef.current?.stopScroll();
            return;
        }

        if (prevScrollHeightRef.current === null) return;
        if (messages.length <= prevMsgCountRef.current) return;

        const delta = container.scrollHeight - prevScrollHeightRef.current;
        if (delta > 0) {
            container.scrollTop += delta;
        }
        stickyContextRef.current?.stopScroll();

        // Mark paginated-in ids so a wrapper can animate them in.
        if (prevMsgCountRef.current > 0) {
            const oldIds = new Set(messages.slice(messages.length - prevMsgCountRef.current).map((m) => m.id));
            const newIds = new Set<string>();
            for (const msg of messages) {
                if (!oldIds.has(msg.id)) newIds.add(msg.id);
            }
            if (newIds.size > 0) {
                setPaginatedNewIds(newIds);
                setTimeout(() => setPaginatedNewIds(new Set()), 600);
            }
        }

        prevScrollHeightRef.current = null;
        prevMsgCountRef.current = 0;
    }, [isFetchingNextPage, messages, getScrollContainer, stickyContextRef]);

    // ── Initial scroll-to-bottom ──────────────────────────────────────
    useEffect(() => {
        if (messages.length === 0 || isLoadingMessages) return;
        if (hasScrolledToBottomRef.current) return;

        const container = getScrollContainer();
        if (!container) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                stickyContextRef.current?.scrollToBottom?.('instant');
                container.scrollTop = container.scrollHeight;
                hasScrolledToBottomRef.current = true;
            });
        });
    }, [messages.length, isLoadingMessages, getScrollContainer, stickyContextRef]);

    /** Drop all pagination / initial-scroll state. Call on session switch. */
    const reset = useCallback(() => {
        hasScrolledToBottomRef.current = false;
        prevScrollHeightRef.current = null;
        prevMsgCountRef.current = 0;
    }, []);

    return {
        paginatedNewIds,
        getScrollContainer,
        reset,
    };
}
